/**
 * ChallengeBattlefieldRenderer
 *
 * Specialized PixiJS renderer for Weekly Challenge battlefields.
 * Extends the CombatGridRenderer pattern with support for:
 * - Dynamic weekly visual assets loaded from API
 * - Themed parallax backgrounds
 * - Ground tile variation rendering
 * - Prop placement (pillars, obstacles)
 * - Animated hazard zones (lava, poison, ice, void)
 * - Atmosphere overlay effects (fog, particles, weather)
 *
 * Usage:
 * ```typescript
 * const renderer = new ChallengeBattlefieldRenderer(config);
 * await renderer.initialize();
 * await renderer.loadVisualAssets(visualAssets);
 *
 * // Add to DOM
 * container.appendChild(renderer.getCanvas());
 *
 * // Update loop
 * app.ticker.add((delta) => renderer.update(delta / 60));
 *
 * // Cleanup
 * renderer.destroy();
 * ```
 */

import * as PIXI from 'pixi.js';
import type { AxialCoordinate } from '@drawn-of-war/shared';
import type {
  ChallengeBattlefieldConfig,
  ChallengeVisualAssets,
  ParallaxLayer,
  PropPlacement,
  HazardZone,
  AtmosphereEffect,
  HazardAnimationState,
  AssetLoadProgress,
  LoadProgressCallback,
  ChallengeTextureCache,
} from './ChallengeBattlefieldTypes';
import { calculateBleedBounds, type BleedBounds } from '../../services/terrain/bleed-calculator';
import { createTilingSprite } from '../../services/terrain/tiling-sprite-factory';
import { applyDesaturation } from '../../utils/pixi-filters';
import { DEFAULT_VISUAL_SETTINGS } from '../../config/visual-settings';
import { CombatGridRenderer, type AnimationCompleteEvent, type CreatureProjectileData } from '../CombatGrid/CombatGridRenderer';
import type { AnimationState } from '../../services/unit-animation-state-machine';

/**
 * ChallengeBattlefieldRenderer
 *
 * Renders Weekly Challenge battlefields with themed visual assets.
 * Composes multiple visual layers for rich battlefield environments.
 */
export class ChallengeBattlefieldRenderer {
  // PixiJS application and containers
  private app!: PIXI.Application;
  private worldContainer!: PIXI.Container;
  private parallaxContainer: PIXI.Container;
  private groundContainer: PIXI.Container;
  private hazardContainer: PIXI.Container;
  private propsBackContainer: PIXI.Container;
  private gridContainer!: PIXI.Container;
  private propsFrontContainer: PIXI.Container;
  private atmosphereContainer: PIXI.Container;

  // Internal CombatGridRenderer for creature rendering (composition pattern)
  // This provides all creature rendering methods needed by CombatVisualizationManager
  private combatGridRenderer!: CombatGridRenderer;

  // Configuration
  private config: ChallengeBattlefieldConfig;
  private visualAssets: ChallengeVisualAssets | null = null;

  // Texture cache
  private textureCache: ChallengeTextureCache = {
    parallax: new Map(),
    groundTiles: [],
    props: new Map(),
    hazardFrames: new Map(),
    atmosphereParticles: new Map(),
  };

  // Animation state
  private hazardAnimations: Map<string, HazardAnimationState> = new Map();
  private hazardSprites: Map<string, PIXI.AnimatedSprite[]> = new Map();
  private atmosphereParticles: PIXI.Container[] = [];

  // State flags
  private initialized: boolean = false;
  private assetsLoaded: boolean = false;
  private destroyed: boolean = false;

  // Event callbacks
  private onLoadProgress: LoadProgressCallback | null = null;

  /**
   * Create a new ChallengeBattlefieldRenderer
   *
   * @param config - Renderer configuration
   */
  constructor(config: ChallengeBattlefieldConfig) {
    this.config = config;

    // Create visual asset containers (will be inserted into CombatGridRenderer's worldContainer)
    this.parallaxContainer = new PIXI.Container();
    this.groundContainer = new PIXI.Container();
    this.hazardContainer = new PIXI.Container();
    this.propsBackContainer = new PIXI.Container();
    this.propsFrontContainer = new PIXI.Container();
    this.atmosphereContainer = new PIXI.Container();
  }

  /**
   * Initialize the renderer
   * Must be called before loading assets or rendering
   *
   * @returns Canvas element for DOM attachment
   */
  async initialize(): Promise<HTMLCanvasElement> {
    if (this.destroyed) {
      throw new Error('Renderer was destroyed');
    }

    if (this.initialized) {
      return this.app.canvas;
    }

    // Create CombatGridRenderer for creature rendering
    // This provides all creature methods needed by CombatVisualizationManager
    this.combatGridRenderer = new CombatGridRenderer({
      canvasWidth: this.config.canvasWidth,
      canvasHeight: this.config.canvasHeight,
      hexGridConfig: {
        ...this.config.hexGridConfig,
        orientation: 'flat-top' as const,
      },
      showCoordinates: false,
    });

    // Initialize the CombatGridRenderer
    await this.combatGridRenderer.init();

    if (this.destroyed) {
      this.combatGridRenderer.destroy();
      throw new Error('Renderer was destroyed during initialization');
    }

    // Get references from CombatGridRenderer
    this.app = this.combatGridRenderer.getApp();
    this.worldContainer = this.combatGridRenderer.getWorldContainer();

    // Insert visual asset layers at correct z-indices in worldContainer
    // CombatGridRenderer's worldContainer already has: gridContainer, highlightLayer, creatureLayer, coordinateLayer
    // We need to insert our themed layers BEFORE and AFTER the creature layer
    //
    // Desired layer order (bottom to top):
    // 0. parallaxContainer (backgrounds)
    // 1. groundContainer (ground tiles)
    // 2. hazardContainer (hazard effects)
    // 3. propsBackContainer (back props)
    // 4. [CombatGridRenderer's gridContainer - terrain]
    // 5. [CombatGridRenderer's creatureLayer - creatures are rendered here]
    // 6. propsFrontContainer (front props)
    // 7. atmosphereContainer (atmosphere effects)
    // 8. [CombatGridRenderer's coordinateLayer]

    // Insert at the beginning (index 0) for background layers
    this.worldContainer.addChildAt(this.parallaxContainer, 0);   // Index 0
    this.worldContainer.addChildAt(this.groundContainer, 1);     // Index 1
    this.worldContainer.addChildAt(this.hazardContainer, 2);     // Index 2
    this.worldContainer.addChildAt(this.propsBackContainer, 3);  // Index 3
    // CombatGridRenderer's gridContainer is now at index 4
    // CombatGridRenderer's highlightLayer is now at index 5
    // CombatGridRenderer's creatureLayer is now at index 6

    // Add front layers at the end (but before coordinate layer if present)
    // Get current children count, subtract 1 to insert before last element (coordinateLayer)
    const insertIndex = Math.max(this.worldContainer.children.length - 1, 0);
    this.worldContainer.addChildAt(this.propsFrontContainer, insertIndex);
    this.worldContainer.addChild(this.atmosphereContainer); // Add at the very end

    // Store reference to grid container for compatibility
    this.gridContainer = this.combatGridRenderer.getWorldContainer().getChildByName?.('gridContainer') as PIXI.Container || new PIXI.Container();

    // Setup animation update loop for themed assets
    this.app.ticker.add(this.updateAnimations, this);

    this.initialized = true;
    console.log('[ChallengeBattlefieldRenderer] Initialized with CombatGridRenderer composition');
    console.log('[ChallengeBattlefieldRenderer] WorldContainer children:', this.worldContainer.children.length);

    return this.app.canvas;
  }

  /**
   * Load and apply visual assets for a weekly challenge
   *
   * @param assets - Visual asset configuration from API
   * @param onProgress - Optional progress callback
   */
  async loadVisualAssets(
    assets: ChallengeVisualAssets,
    onProgress?: LoadProgressCallback
  ): Promise<void> {
    if (!this.initialized) {
      throw new Error('Renderer must be initialized before loading assets');
    }

    if (this.destroyed) {
      throw new Error('Renderer was destroyed');
    }

    this.visualAssets = assets;
    this.onLoadProgress = onProgress || null;

    // Calculate total assets to load
    const totalAssets =
      assets.parallaxLayers.length +
      assets.groundTiles.variations.length +
      assets.props.length +
      assets.hazards.reduce((sum, h) => sum + h.frames.length, 0) +
      assets.atmosphere.filter((a) => a.textureUrl).length;

    let loadedCount = 0;

    const reportProgress = (phase: AssetLoadProgress['phase'], currentAsset: string) => {
      if (this.onLoadProgress) {
        this.onLoadProgress({
          total: totalAssets,
          loaded: loadedCount,
          currentAsset,
          phase,
        });
      }
    };

    try {
      // Phase 1: Load parallax layers
      reportProgress('parallax', 'Loading backgrounds...');
      await this.loadParallaxLayers(assets.parallaxLayers, () => {
        loadedCount++;
        reportProgress('parallax', `Loaded parallax layer ${loadedCount}`);
      });

      // Phase 2: Load ground tiles
      reportProgress('ground', 'Loading ground tiles...');
      await this.loadGroundTiles(assets.groundTiles, () => {
        loadedCount++;
        reportProgress('ground', `Loaded ground tile ${loadedCount}`);
      });

      // Phase 3: Load props
      reportProgress('props', 'Loading props...');
      await this.loadProps(assets.props, () => {
        loadedCount++;
        reportProgress('props', `Loaded prop ${loadedCount}`);
      });

      // Phase 4: Load hazards
      reportProgress('hazards', 'Loading hazards...');
      await this.loadHazards(assets.hazards, () => {
        loadedCount++;
        reportProgress('hazards', `Loaded hazard frame ${loadedCount}`);
      });

      // Phase 5: Load atmosphere
      reportProgress('atmosphere', 'Loading atmosphere effects...');
      await this.loadAtmosphereEffects(assets.atmosphere, () => {
        loadedCount++;
        reportProgress('atmosphere', `Loaded atmosphere texture ${loadedCount}`);
      });

      // Render all layers
      this.renderAllLayers();

      this.assetsLoaded = true;
      reportProgress('complete', 'All assets loaded');
    } catch (error) {
      console.error('[ChallengeBattlefieldRenderer] Asset loading failed:', error);
      throw error;
    }
  }

  /**
   * Load a background image from MapConfig.visual settings.
   * Used when the challenge map has a hand-positioned background from the battlefield editor.
   * This is independent of the themed ChallengeVisualAssets pipeline.
   */
  async loadMapVisual(visual: {
    backgroundImage: string;
    bgOffsetX: number;
    bgOffsetY: number;
    bgScale: number;
    bgBrightness?: number;
    bgContrast?: number;
    bgSaturation?: number;
    bgHue?: number;
  }): Promise<void> {
    if (!this.initialized) {
      throw new Error('Renderer must be initialized before loading map visual');
    }

    try {
      // Load the background image
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous';
      bgImg.src = visual.backgroundImage;
      await new Promise<void>((resolve, reject) => {
        bgImg.onload = () => resolve();
        bgImg.onerror = () => reject(new Error(`Failed to load bg: ${visual.backgroundImage}`));
      });

      const bgTexture = PIXI.Texture.from(bgImg);
      const bgSprite = new PIXI.Sprite(bgTexture);

      // Apply scale and anchor
      bgSprite.scale.set(visual.bgScale);
      bgSprite.anchor.set(0.5, 0.5);

      // Position at grid center + offset (same formula as DeploymentGridRenderer)
      const gridBounds = this.getGridBounds();
      const gridOffset = this.getGridOffset();
      const gridCenterX = gridOffset.x + gridBounds.minX + gridBounds.width / 2;
      const gridCenterY = gridOffset.y + gridBounds.minY + gridBounds.height / 2;
      bgSprite.x = gridCenterX + (visual.bgOffsetX ?? 0);
      bgSprite.y = gridCenterY + (visual.bgOffsetY ?? 0);

      // Apply color adjustments
      const brightness = visual.bgBrightness ?? 1.0;
      const contrast = visual.bgContrast ?? 1.0;
      const saturation = visual.bgSaturation ?? 1.0;
      const hue = visual.bgHue ?? 0;
      const hasColorAdjustments = brightness !== 1.0 || contrast !== 1.0 || saturation !== 1.0 || hue !== 0;

      if (hasColorAdjustments) {
        const colorFilter = new PIXI.ColorMatrixFilter();
        colorFilter.reset();
        colorFilter.brightness(brightness, true);
        colorFilter.contrast(contrast, true);
        colorFilter.saturate(saturation, true);
        if (hue !== 0) {
          colorFilter.hue(hue, true);
        }
        bgSprite.filters = [colorFilter];
      }

      // Hide the default terrain background so the custom map visual is visible
      this.combatGridRenderer.hideTerrainBackground();

      // Insert into parallax container (behind everything else)
      this.parallaxContainer.addChildAt(bgSprite, 0);

      // Limit zoom-out so the background always fills the viewport.
      // At zoom level Z, the visible world area is (canvasWidth/Z) × (canvasHeight/Z).
      // The background covers (textureWidth * bgScale) × (textureHeight * bgScale) in world space.
      // To prevent grey borders: Z >= canvasWidth / bgWorldWidth AND Z >= canvasHeight / bgWorldHeight.
      const bgWorldWidth = bgTexture.width * visual.bgScale;
      const bgWorldHeight = bgTexture.height * visual.bgScale;
      const minZoomX = this.config.canvasWidth / bgWorldWidth;
      const minZoomY = this.config.canvasHeight / bgWorldHeight;
      const bgMinZoom = Math.max(minZoomX, minZoomY);

      // Background world-space bounding box (sprite is anchored at center)
      const bgWorldBounds = {
        left: bgSprite.x - bgWorldWidth / 2,
        right: bgSprite.x + bgWorldWidth / 2,
        top: bgSprite.y - bgWorldHeight / 2,
        bottom: bgSprite.y + bgWorldHeight / 2,
      };

      const camera = this.combatGridRenderer.getCameraManager();
      if (camera) {
        camera.updateConfig({
          minZoom: bgMinZoom,
          bgWorldBounds,
        });
        // If the current zoom is below the new minimum, clamp it up
        if (camera.getZoom() < bgMinZoom) {
          camera.setZoom(bgMinZoom);
          camera.applyTransform();
        }
      }

      console.log('[ChallengeBattlefieldRenderer] Map visual background loaded:', {
        image: visual.backgroundImage,
        scale: visual.bgScale,
        offset: `(${visual.bgOffsetX}, ${visual.bgOffsetY})`,
        colorAdjustments: hasColorAdjustments,
        bgMinZoom: bgMinZoom.toFixed(3),
      });
    } catch (error) {
      console.warn('[ChallengeBattlefieldRenderer] Failed to load map visual background:', error);
    }
  }

  /**
   * Load per-hex tile assignments onto the internal CombatGridRenderer.
   * Delegates to CombatGridRenderer.loadTiles() which handles texture loading,
   * hex-clipped base tiles, Y-sorted overlays, and camera constraints.
   */
  async loadTiles(tiles: Record<string, { baseTile: string; overlay?: string }>): Promise<void> {
    if (!this.initialized || !this.combatGridRenderer) {
      console.warn('[ChallengeBattlefieldRenderer] Cannot load tiles before initialization');
      return;
    }
    await this.combatGridRenderer.loadTiles(tiles);

    // Load default background behind tiles if no custom background was already set
    if (this.parallaxContainer.children.length === 0) {
      await this.loadDefaultBackground();
    }

    console.log('[ChallengeBattlefieldRenderer] Tiles loaded:', Object.keys(tiles).length, 'hexes');
  }

  /**
   * Load the default challenge background image behind hex tiles.
   * Zooms into the bottom portion of the image to show the atmospheric
   * water/crystal scene and push the horizon line off-screen.
   */
  private async loadDefaultBackground(): Promise<void> {
    const BG_PATH = '/assets/challenges/volcanic/battlefield-bg.jpeg';
    try {
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous';
      bgImg.src = BG_PATH;
      await new Promise<void>((resolve, reject) => {
        bgImg.onload = () => resolve();
        bgImg.onerror = () => reject(new Error(`Failed to load default bg: ${BG_PATH}`));
      });

      const bgTexture = PIXI.Texture.from(bgImg);
      const bgSprite = new PIXI.Sprite(bgTexture);

      // Anchor near the bottom of the image to focus on the atmospheric scene
      // and push the sky/horizon off the top of the screen
      bgSprite.anchor.set(0.5, 0.85);

      // Position at grid center
      const gridBounds = this.getGridBounds();
      const gridOffset = this.getGridOffset();
      const gridCenterX = gridOffset.x + gridBounds.minX + gridBounds.width / 2;
      const gridCenterY = gridOffset.y + gridBounds.minY + gridBounds.height / 2;
      bgSprite.x = gridCenterX;
      bgSprite.y = gridCenterY;

      // Scale to cover the full canvas with the bottom portion of the image.
      // We need the visible area (roughly bottom 40% of image) to fill the viewport.
      const scaleX = this.config.canvasWidth / bgTexture.width;
      const scaleY = this.config.canvasHeight / (bgTexture.height * 0.4);
      const bgScale = Math.max(scaleX, scaleY) * 1.15; // slight extra to ensure full coverage
      bgSprite.scale.set(bgScale);

      // Darken slightly so it doesn't compete with the hex tiles and creatures
      const colorFilter = new PIXI.ColorMatrixFilter();
      colorFilter.reset();
      colorFilter.brightness(0.6, true);
      colorFilter.saturate(0.7, true);
      bgSprite.filters = [colorFilter];

      this.parallaxContainer.addChildAt(bgSprite, 0);

      console.log('[ChallengeBattlefieldRenderer] Default background loaded:', {
        scale: bgScale.toFixed(3),
        center: `(${gridCenterX.toFixed(0)}, ${gridCenterY.toFixed(0)})`,
      });
    } catch (error) {
      console.warn('[ChallengeBattlefieldRenderer] Failed to load default background:', error);
    }
  }

  /**
   * Load parallax background layers
   */
  private async loadParallaxLayers(
    layers: ParallaxLayer[],
    onLoaded: () => void
  ): Promise<void> {
    // Sort by depth (far to near)
    const sortedLayers = [...layers].sort((a, b) => a.depth - b.depth);

    for (const layer of sortedLayers) {
      try {
        const texture = await PIXI.Assets.load(layer.url);
        this.textureCache.parallax.set(layer.depth, texture);
        onLoaded();
      } catch (error) {
        console.warn(`[ChallengeBattlefieldRenderer] Failed to load parallax layer ${layer.depth}:`, error);
        // Create placeholder texture
        const placeholder = this.createPlaceholderTexture(layer.depth);
        this.textureCache.parallax.set(layer.depth, placeholder);
        onLoaded();
      }
    }
  }

  /**
   * Load ground tile variations
   */
  private async loadGroundTiles(
    config: ChallengeVisualAssets['groundTiles'],
    onLoaded: () => void
  ): Promise<void> {
    this.textureCache.groundTiles = [];

    for (const variation of config.variations) {
      try {
        const texture = await PIXI.Assets.load(variation.url);
        this.textureCache.groundTiles.push(texture);
        onLoaded();
      } catch (error) {
        console.warn(`[ChallengeBattlefieldRenderer] Failed to load ground tile ${variation.index}:`, error);
        // Create placeholder
        const placeholder = PIXI.Texture.WHITE;
        this.textureCache.groundTiles.push(placeholder);
        onLoaded();
      }
    }
  }

  /**
   * Load prop sprites
   */
  private async loadProps(
    props: PropPlacement[],
    onLoaded: () => void
  ): Promise<void> {
    for (const prop of props) {
      try {
        const texture = await PIXI.Assets.load(prop.url);
        this.textureCache.props.set(prop.id, texture);
        onLoaded();
      } catch (error) {
        console.warn(`[ChallengeBattlefieldRenderer] Failed to load prop ${prop.id}:`, error);
        onLoaded();
      }
    }
  }

  /**
   * Load hazard animation frames
   */
  private async loadHazards(
    hazards: HazardZone[],
    onLoaded: () => void
  ): Promise<void> {
    for (const hazard of hazards) {
      const frames: PIXI.Texture[] = [];

      for (const frameUrl of hazard.frames) {
        try {
          const texture = await PIXI.Assets.load(frameUrl);
          frames.push(texture);
          onLoaded();
        } catch (error) {
          console.warn(`[ChallengeBattlefieldRenderer] Failed to load hazard frame for ${hazard.id}:`, error);
          // Use placeholder for missing frame
          frames.push(this.createHazardPlaceholder(hazard.type));
          onLoaded();
        }
      }

      this.textureCache.hazardFrames.set(hazard.id, frames);

      // Initialize animation state
      this.hazardAnimations.set(hazard.id, {
        hazardId: hazard.id,
        currentFrame: 0,
        elapsedTime: 0,
        paused: false,
      });
    }
  }

  /**
   * Load atmosphere effect textures
   */
  private async loadAtmosphereEffects(
    effects: AtmosphereEffect[],
    onLoaded: () => void
  ): Promise<void> {
    for (const effect of effects) {
      if (effect.textureUrl) {
        try {
          const texture = await PIXI.Assets.load(effect.textureUrl);
          this.textureCache.atmosphereParticles.set(effect.type, texture);
          onLoaded();
        } catch (error) {
          console.warn(`[ChallengeBattlefieldRenderer] Failed to load atmosphere texture for ${effect.type}:`, error);
          onLoaded();
        }
      }
    }
  }

  /**
   * Render all visual layers
   */
  private renderAllLayers(): void {
    if (!this.visualAssets) return;

    this.renderParallaxLayers();
    this.renderGroundTiles();
    this.renderHazards();
    this.renderProps();
    this.renderAtmosphere();

    // Apply background desaturation to improve visual hierarchy
    this.applyBackgroundDesaturation();
  }

  /**
   * Render parallax background layers
   */
  private renderParallaxLayers(): void {
    if (!this.visualAssets) return;

    this.parallaxContainer.removeChildren();

    const bounds = calculateBleedBounds(this.config.gridBounds, 3.0);

    for (const layer of this.visualAssets.parallaxLayers) {
      const texture = this.textureCache.parallax.get(layer.depth);
      if (!texture) continue;

      let sprite: PIXI.TilingSprite | PIXI.Sprite;

      if (layer.tileX || layer.tileY) {
        // Create tiling sprite for seamless backgrounds
        sprite = new PIXI.TilingSprite({
          texture,
          width: bounds.width,
          height: bounds.height,
        });
        sprite.tileScale.set(layer.scale, layer.scale);
      } else {
        // Single sprite for non-tiling layers
        sprite = new PIXI.Sprite(texture);
        const scaleX = bounds.width / texture.width;
        const scaleY = bounds.height / texture.height;
        const scale = Math.max(scaleX, scaleY) * layer.scale;
        sprite.scale.set(scale, scale);
      }

      sprite.alpha = layer.alpha;
      sprite.x = bounds.minX;
      sprite.y = bounds.minY;

      // Store parallax factor for camera updates
      (sprite as any).parallaxFactor = layer.parallaxFactor;

      this.parallaxContainer.addChild(sprite);
    }
  }

  /**
   * Render ground tiles with variation
   */
  private renderGroundTiles(): void {
    if (!this.visualAssets || this.textureCache.groundTiles.length === 0) return;

    this.groundContainer.removeChildren();

    const bounds = this.config.gridBounds;
    const tileTexture = this.textureCache.groundTiles[0]; // Primary tile

    // Create tiling sprite for ground
    const groundSprite = new PIXI.TilingSprite({
      texture: tileTexture,
      width: bounds.maxX - bounds.minX + 200, // Add padding
      height: bounds.maxY - bounds.minY + 200,
    });

    const scale = this.visualAssets.groundTiles.scale || 1.0;
    groundSprite.tileScale.set(scale, scale);
    groundSprite.x = bounds.minX - 100;
    groundSprite.y = bounds.minY - 100;

    this.groundContainer.addChild(groundSprite);

    // Add variation overlays for visual interest
    if (this.textureCache.groundTiles.length > 1) {
      this.addGroundVariations();
    }
  }

  /**
   * Add ground tile variations for visual interest
   */
  private addGroundVariations(): void {
    if (!this.visualAssets) return;

    const bounds = this.config.gridBounds;
    const variations = this.textureCache.groundTiles.slice(1);

    // Scatter variation tiles randomly across the ground
    const numVariations = 15;
    for (let i = 0; i < numVariations; i++) {
      const texture = variations[Math.floor(Math.random() * variations.length)];
      const sprite = new PIXI.Sprite(texture);

      sprite.anchor.set(0.5, 0.5);
      sprite.x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
      sprite.y = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
      sprite.alpha = 0.3 + Math.random() * 0.3; // Subtle overlay
      sprite.rotation = Math.random() * Math.PI * 2;
      sprite.scale.set(0.5 + Math.random() * 0.5);

      this.groundContainer.addChild(sprite);
    }
  }

  /**
   * Render hazard zones with animations
   */
  private renderHazards(): void {
    if (!this.visualAssets) return;

    this.hazardContainer.removeChildren();
    this.hazardSprites.clear();

    for (const hazard of this.visualAssets.hazards) {
      const frames = this.textureCache.hazardFrames.get(hazard.id);
      if (!frames || frames.length === 0) continue;

      const sprites: PIXI.AnimatedSprite[] = [];

      // Create animated sprite for each hex position
      for (const position of hazard.positions) {
        const animatedSprite = new PIXI.AnimatedSprite(frames);

        // Configure animation
        animatedSprite.animationSpeed = 1000 / hazard.frameDuration / 60; // Convert to ticker speed
        animatedSprite.loop = true;
        animatedSprite.anchor.set(0.5, 0.5);

        // Position at hex center
        const pixel = this.config.hexToPixel(position);
        animatedSprite.x = pixel.x;
        animatedSprite.y = pixel.y;

        // Apply hazard-specific styling
        if (hazard.glowColor) {
          animatedSprite.tint = hazard.glowColor;
        }
        animatedSprite.alpha = hazard.glowIntensity ?? 0.8;

        // Scale to fit hex
        const hexSize = this.config.hexGridConfig.hexSize;
        const scale = (hexSize * 2) / Math.max(frames[0].width, frames[0].height);
        animatedSprite.scale.set(scale, scale);

        // Start animation if enabled
        if (this.config.animateHazards !== false) {
          animatedSprite.play();
        }

        this.hazardContainer.addChild(animatedSprite);
        sprites.push(animatedSprite);
      }

      this.hazardSprites.set(hazard.id, sprites);
    }
  }

  /**
   * Render prop sprites
   */
  private renderProps(): void {
    if (!this.visualAssets) return;

    this.propsBackContainer.removeChildren();
    this.propsFrontContainer.removeChildren();

    // Sort props by zIndex for proper layering
    const sortedProps = [...this.visualAssets.props].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

    for (const prop of sortedProps) {
      const texture = this.textureCache.props.get(prop.id);
      if (!texture) continue;

      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5, 1.0); // Anchor at bottom center

      // Position at hex with optional offset
      const pixel = this.config.hexToPixel(prop.position);
      sprite.x = pixel.x + (prop.offset?.x ?? 0);
      sprite.y = pixel.y + (prop.offset?.y ?? 0);

      // Apply scale
      sprite.scale.set(prop.scale ?? 1.0);

      // Add to appropriate container based on zIndex
      // Props with zIndex < 0 go behind grid, others go in front
      if ((prop.zIndex ?? 0) < 0) {
        this.propsBackContainer.addChild(sprite);
      } else {
        this.propsFrontContainer.addChild(sprite);
      }
    }
  }

  /**
   * Render atmosphere effects
   */
  private renderAtmosphere(): void {
    if (!this.visualAssets || this.config.enableAtmosphere === false) return;

    this.atmosphereContainer.removeChildren();
    this.atmosphereParticles = [];

    const bounds = this.config.gridBounds;

    for (const effect of this.visualAssets.atmosphere) {
      switch (effect.type) {
        case 'fog':
          this.createFogEffect(effect, bounds);
          break;
        case 'rain':
        case 'snow':
        case 'ash':
        case 'dust':
          this.createParticleEffect(effect, bounds);
          break;
        case 'sparkles':
          this.createSparkleEffect(effect, bounds);
          break;
        case 'lightning':
          // Lightning is handled separately as a flash effect
          break;
        default:
          // Full-screen overlay
          if (effect.overlayColor !== undefined) {
            this.createOverlayEffect(effect, bounds);
          }
      }
    }
  }

  /**
   * Create fog atmosphere effect
   */
  private createFogEffect(effect: AtmosphereEffect, bounds: ChallengeBattlefieldConfig['gridBounds']): void {
    const fogContainer = new PIXI.Container();
    const density = effect.density ?? 20;

    for (let i = 0; i < density; i++) {
      const texture = this.textureCache.atmosphereParticles.get('fog') ?? PIXI.Texture.WHITE;
      const particle = new PIXI.Sprite(texture);

      particle.anchor.set(0.5, 0.5);
      particle.x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
      particle.y = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
      particle.alpha = (effect.intensity ?? 0.5) * (0.3 + Math.random() * 0.4);
      particle.scale.set(2 + Math.random() * 3);
      particle.tint = effect.tint ?? 0xffffff;

      // Store velocity for animation
      (particle as any).vx = (Math.random() - 0.5) * (effect.speed ?? 20);
      (particle as any).vy = (Math.random() - 0.5) * (effect.speed ?? 20) * 0.5;

      fogContainer.addChild(particle);
    }

    this.atmosphereContainer.addChild(fogContainer);
    this.atmosphereParticles.push(fogContainer);
  }

  /**
   * Create particle-based atmosphere effect (rain, snow, ash, dust)
   */
  private createParticleEffect(effect: AtmosphereEffect, bounds: ChallengeBattlefieldConfig['gridBounds']): void {
    const particleContainer = new PIXI.Container();
    const density = effect.density ?? 50;

    for (let i = 0; i < density; i++) {
      const texture = this.textureCache.atmosphereParticles.get(effect.type) ?? PIXI.Texture.WHITE;
      const particle = new PIXI.Sprite(texture);

      particle.anchor.set(0.5, 0.5);
      particle.x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
      particle.y = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
      particle.alpha = effect.intensity ?? 0.6;
      particle.tint = effect.tint ?? 0xffffff;

      // Size based on effect type
      const baseSize = effect.type === 'rain' ? 0.5 : effect.type === 'snow' ? 1.0 : 0.3;
      particle.scale.set(baseSize + Math.random() * baseSize);

      // Velocity based on direction and speed
      const direction = effect.direction ?? Math.PI / 2; // Default: downward
      const speed = effect.speed ?? 100;
      (particle as any).vx = Math.cos(direction) * speed * (0.8 + Math.random() * 0.4);
      (particle as any).vy = Math.sin(direction) * speed * (0.8 + Math.random() * 0.4);
      (particle as any).bounds = bounds;

      particleContainer.addChild(particle);
    }

    this.atmosphereContainer.addChild(particleContainer);
    this.atmosphereParticles.push(particleContainer);
  }

  /**
   * Create sparkle/glitter effect
   */
  private createSparkleEffect(effect: AtmosphereEffect, bounds: ChallengeBattlefieldConfig['gridBounds']): void {
    const sparkleContainer = new PIXI.Container();
    const density = effect.density ?? 30;

    for (let i = 0; i < density; i++) {
      const sparkle = new PIXI.Graphics();
      sparkle.circle(0, 0, 2);
      sparkle.fill({ color: effect.tint ?? 0xffffff, alpha: 1 });

      sparkle.x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
      sparkle.y = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
      sparkle.alpha = 0;

      // Store animation state
      (sparkle as any).phase = Math.random() * Math.PI * 2;
      (sparkle as any).maxAlpha = (effect.intensity ?? 0.8) * (0.5 + Math.random() * 0.5);

      sparkleContainer.addChild(sparkle);
    }

    this.atmosphereContainer.addChild(sparkleContainer);
    this.atmosphereParticles.push(sparkleContainer);
  }

  /**
   * Create full-screen color overlay effect
   */
  private createOverlayEffect(effect: AtmosphereEffect, bounds: ChallengeBattlefieldConfig['gridBounds']): void {
    const overlay = new PIXI.Graphics();
    overlay.rect(
      bounds.minX - 100,
      bounds.minY - 100,
      bounds.maxX - bounds.minX + 200,
      bounds.maxY - bounds.minY + 200
    );
    overlay.fill({ color: effect.overlayColor!, alpha: effect.intensity ?? 0.2 });

    // Apply blend mode
    switch (effect.blendMode) {
      case 'add':
        overlay.blendMode = 'add';
        break;
      case 'multiply':
        overlay.blendMode = 'multiply';
        break;
      case 'screen':
        overlay.blendMode = 'screen';
        break;
      default:
        overlay.blendMode = 'normal';
    }

    this.atmosphereContainer.addChild(overlay);
  }

  /**
   * Animation update callback (runs every frame)
   */
  private updateAnimations = (ticker: PIXI.Ticker): void => {
    if (!this.assetsLoaded || this.destroyed) return;

    const deltaSeconds = ticker.deltaMS / 1000;

    // Update atmosphere particles
    this.updateAtmosphereParticles(deltaSeconds);
  };

  /**
   * Update atmosphere particle positions
   */
  private updateAtmosphereParticles(deltaSeconds: number): void {
    for (const container of this.atmosphereParticles) {
      for (const child of container.children) {
        const particle = child as PIXI.Sprite & {
          vx?: number;
          vy?: number;
          phase?: number;
          maxAlpha?: number;
          bounds?: ChallengeBattlefieldConfig['gridBounds'];
        };

        // Update position for moving particles
        if (particle.vx !== undefined && particle.vy !== undefined) {
          particle.x += particle.vx * deltaSeconds;
          particle.y += particle.vy * deltaSeconds;

          // Wrap around bounds
          const bounds = particle.bounds ?? this.config.gridBounds;
          if (particle.y > bounds.maxY + 50) {
            particle.y = bounds.minY - 50;
            particle.x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
          }
          if (particle.x > bounds.maxX + 50) {
            particle.x = bounds.minX - 50;
          }
          if (particle.x < bounds.minX - 50) {
            particle.x = bounds.maxX + 50;
          }
        }

        // Update sparkle alpha
        if (particle.phase !== undefined && particle.maxAlpha !== undefined) {
          particle.phase += deltaSeconds * 3;
          particle.alpha = Math.max(0, Math.sin(particle.phase)) * particle.maxAlpha;
        }
      }
    }
  }

  /**
   * Update camera position for parallax effects
   *
   * @param cameraX - Camera X position
   * @param cameraY - Camera Y position
   * @param zoom - Camera zoom level
   */
  updateCameraPosition(cameraX: number, cameraY: number, zoom: number): void {
    // Update parallax layer positions
    for (const child of this.parallaxContainer.children) {
      const sprite = child as PIXI.Sprite & { parallaxFactor?: number };
      if (sprite.parallaxFactor !== undefined) {
        // Apply parallax offset based on camera position
        const offsetX = cameraX * (1 - sprite.parallaxFactor);
        const offsetY = cameraY * (1 - sprite.parallaxFactor);
        sprite.x = this.config.gridBounds.minX - 100 + offsetX;
        sprite.y = this.config.gridBounds.minY - 100 + offsetY;
      }
    }
  }

  /**
   * Manually trigger update (for external control)
   *
   * @param deltaTime - Time since last update in seconds
   */
  update(deltaTime: number): void {
    if (this.assetsLoaded && !this.destroyed) {
      this.updateAtmosphereParticles(deltaTime);
    }
  }

  /**
   * Get grid container for adding hex grid elements
   */
  getGridContainer(): PIXI.Container {
    return this.gridContainer;
  }

  /**
   * Get world container for camera transforms
   */
  getWorldContainer(): PIXI.Container {
    return this.worldContainer;
  }

  /**
   * Get the canvas element
   */
  getCanvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  /**
   * Get the PixiJS application instance
   */
  getApp(): PIXI.Application {
    return this.app;
  }

  // =============================================================================
  // CREATURE RENDERING DELEGATE METHODS
  // These delegate to the internal CombatGridRenderer for creature rendering
  // Required by CombatVisualizationManager
  // =============================================================================

  /**
   * Get the stage (PIXI container) for the visualization manager
   * Delegates to CombatGridRenderer
   */
  getStage(): PIXI.Container {
    return this.combatGridRenderer.getStage();
  }

  /**
   * Check if animations are supported
   * Delegates to CombatGridRenderer
   */
  supportsAnimations(): boolean {
    return this.combatGridRenderer.supportsAnimations();
  }

  /**
   * Render a creature with a specific unit ID
   * Delegates to CombatGridRenderer
   */
  renderCreatureWithId(
    unitId: string,
    hex: AxialCoordinate,
    creatureId: string,
    playerId: 'player1' | 'player2',
    spriteData?: any,
    alpha?: number,
    facingDirection?: number,
    sizeMultiplier?: number
  ): void {
    this.combatGridRenderer.renderCreatureWithId(
      unitId,
      hex,
      creatureId,
      playerId,
      spriteData,
      alpha,
      facingDirection,
      sizeMultiplier
    );
  }

  /**
   * Render a creature at a position (fallback method)
   * Delegates to CombatGridRenderer
   */
  renderCreature(
    hex: AxialCoordinate,
    creatureId: string,
    playerId: 'player1' | 'player2',
    spriteData?: any,
    alpha?: number,
    facingDirection?: number
  ): void {
    this.combatGridRenderer.renderCreature(hex, creatureId, playerId, spriteData, alpha, facingDirection);
  }

  /**
   * Get sprite data by unit ID
   * Delegates to CombatGridRenderer
   */
  getSpriteByUnitId(unitId: string): any {
    return this.combatGridRenderer.getSpriteByUnitId(unitId);
  }

  /**
   * Get sprite at a hex position
   * Delegates to CombatGridRenderer
   */
  getSpriteAt(hex: AxialCoordinate): any {
    return this.combatGridRenderer.getSpriteAt(hex);
  }

  /**
   * Get projectile data for a unit
   * Delegates to CombatGridRenderer
   */
  getProjectileDataByUnitId(unitId: string): CreatureProjectileData | undefined {
    return this.combatGridRenderer.getProjectileDataByUnitId(unitId);
  }

  /**
   * Get all active sprites
   * Delegates to CombatGridRenderer
   */
  getActiveSprites(): Map<string, any> {
    return this.combatGridRenderer.getActiveSprites();
  }

  /**
   * Change animation state for a unit
   * Delegates to CombatGridRenderer
   */
  async changeAnimationState(unitId: string, newState: AnimationState): Promise<void> {
    return this.combatGridRenderer.changeAnimationState(unitId, newState);
  }

  /**
   * Apply a wind-up offset to a unit's sprite (temporary lean toward/away from target).
   * Delegates to CombatGridRenderer
   */
  applyWindupOffset(unitId: string, offsetX: number, offsetY: number, durationMs: number): void {
    this.combatGridRenderer.applyWindupOffset(unitId, offsetX, offsetY, durationMs);
  }

  /**
   * Flash a sprite (hit effect)
   * Delegates to CombatGridRenderer
   */
  flashSprite(unitId: string, color?: number, duration?: number): void {
    this.combatGridRenderer.flashSprite(unitId, color, duration);
  }

  /**
   * Remove a creature by unit ID
   * Delegates to CombatGridRenderer
   */
  removeCreatureById(unitId: string): void {
    this.combatGridRenderer.removeCreatureById(unitId);
  }

  /**
   * Register animation complete callback
   * Delegates to CombatGridRenderer
   */
  onAnimationComplete(callback: (event: AnimationCompleteEvent) => void): void {
    this.combatGridRenderer.onAnimationComplete(callback);
  }

  /**
   * Convert hex to pixel coordinates
   * Delegates to CombatGridRenderer
   */
  hexToPixel(hex: AxialCoordinate): { x: number; y: number } {
    return this.combatGridRenderer.hexToPixel(hex);
  }

  /**
   * Update animations
   * Delegates to CombatGridRenderer for creature animations
   */
  updateCreatureAnimations(deltaTime: number): void {
    this.combatGridRenderer.updateAnimations(deltaTime);
  }

  // =============================================================================
  // CAMERA AND GRID DELEGATE METHODS
  // =============================================================================

  /**
   * Get the camera manager
   * Delegates to CombatGridRenderer
   */
  getCameraManager(): any {
    return this.combatGridRenderer.getCameraManager();
  }

  /**
   * Get the grid offset
   * Delegates to CombatGridRenderer
   */
  getGridOffset(): { x: number; y: number } {
    return this.combatGridRenderer.getGridOffset();
  }

  /**
   * Get the grid bounds
   * Delegates to CombatGridRenderer
   */
  getGridBounds(): { width: number; height: number; minX: number; minY: number } {
    return this.combatGridRenderer.getGridBounds();
  }

  /**
   * Zoom in
   * Delegates to CombatGridRenderer
   */
  zoomIn(): void {
    this.combatGridRenderer.zoomIn();
  }

  /**
   * Zoom out
   * Delegates to CombatGridRenderer
   */
  zoomOut(): void {
    this.combatGridRenderer.zoomOut();
  }

  /**
   * Zoom at a specific point
   * Delegates to CombatGridRenderer
   */
  zoomAtPoint(targetZoom: number, screenPos: { x: number; y: number }): void {
    this.combatGridRenderer.zoomAtPoint(targetZoom, screenPos);
  }

  // =============================================================================
  // END CAMERA AND GRID DELEGATE METHODS
  // =============================================================================

  /**
   * Check if a hex position has a hazard
   *
   * @param hex - Hex coordinate to check
   * @returns Hazard zone if present, undefined otherwise
   */
  getHazardAtHex(hex: AxialCoordinate): HazardZone | undefined {
    if (!this.visualAssets) return undefined;

    return this.visualAssets.hazards.find((hazard) =>
      hazard.positions.some((pos) => pos.q === hex.q && pos.r === hex.r)
    );
  }

  /**
   * Check if a hex position is blocked by a prop
   *
   * @param hex - Hex coordinate to check
   * @returns true if blocked, false otherwise
   */
  isHexBlockedByProp(hex: AxialCoordinate): boolean {
    if (!this.visualAssets) return false;

    return this.visualAssets.props.some(
      (prop) =>
        prop.blocking &&
        prop.position.q === hex.q &&
        prop.position.r === hex.r
    );
  }

  /**
   * Resize the renderer
   *
   * @param width - New canvas width
   * @param height - New canvas height
   */
  resize(width: number, height: number): void {
    if (this.destroyed) return;

    this.config.canvasWidth = width;
    this.config.canvasHeight = height;
    this.app.renderer.resize(width, height);
  }

  /**
   * Create a placeholder texture for missing parallax layers
   */
  private createPlaceholderTexture(depth: number): PIXI.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    // Gradient based on depth
    const gradient = ctx.createLinearGradient(0, 0, 0, 128);
    if (depth === 0) {
      gradient.addColorStop(0, '#0a0a0a');
      gradient.addColorStop(1, '#1a1a2e');
    } else if (depth === 1) {
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
    } else {
      gradient.addColorStop(0, '#16213e');
      gradient.addColorStop(1, '#1a1a2e');
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 128, 128);

    return PIXI.Texture.from(canvas);
  }

  /**
   * Create a placeholder texture for missing hazard frames
   */
  private createHazardPlaceholder(type: HazardZone['type']): PIXI.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Color based on hazard type
    const colors: Record<HazardZone['type'], string> = {
      lava: '#ff4400',
      poison: '#00ff44',
      ice: '#44ccff',
      void: '#8800ff',
      fire: '#ff8800',
      acid: '#ccff00',
    };

    const color = colors[type] || '#ff0000';
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();

    return PIXI.Texture.from(canvas);
  }

  /**
   * Apply background desaturation to improve visual hierarchy
   * Creatures and effects should "pop" against muted backgrounds
   */
  private applyBackgroundDesaturation(): void {
    const desatConfig = DEFAULT_VISUAL_SETTINGS.backgroundDesaturation;
    if (!desatConfig.enabled) return;

    const { strength, affectsParallax, affectsGround, affectsProps } = desatConfig;

    // Desaturate hex grid background (CRITICAL - the bright green)
    if (this.gridContainer) {
      applyDesaturation(this.gridContainer, strength);
    }

    if (affectsParallax) applyDesaturation(this.parallaxContainer, strength);
    if (affectsGround) applyDesaturation(this.groundContainer, strength);
    if (affectsProps) {
      applyDesaturation(this.propsBackContainer, strength);
      applyDesaturation(this.propsFrontContainer, strength);
    }

    console.log(`[ChallengeBattlefieldRenderer] Applied ${(strength * 100).toFixed(0)}% background desaturation (including grid)`);
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    // Stop animation loop
    if (this.app?.ticker) {
      this.app.ticker.remove(this.updateAnimations, this);
    }

    // Clear hazard animations
    this.hazardAnimations.clear();
    this.hazardSprites.clear();

    // Clear texture cache (don't destroy shared textures)
    this.textureCache.parallax.clear();
    this.textureCache.groundTiles = [];
    this.textureCache.props.clear();
    this.textureCache.hazardFrames.clear();
    this.textureCache.atmosphereParticles.clear();

    // Destroy CombatGridRenderer (which owns the PIXI app)
    if (this.combatGridRenderer) {
      this.combatGridRenderer.destroy();
    }

    this.initialized = false;
    this.assetsLoaded = false;
  }
}
